import React, { createContext, useState, useContext, useEffect } from 'react';
import { lightTheme, darkTheme } from '../styles/theme';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Intentar cargar tema guardado del localStorage
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme;
    }
    
    // Detectar preferencia del sistema
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'Oscuro';
    }
    
    return 'Claro';
  };

  const [themeName, setThemeName] = useState(getInitialTheme);
  const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('primaryColor') || '#5B9BD5');
  const [currentTheme, setCurrentTheme] = useState(
    themeName === 'Oscuro' ? darkTheme : lightTheme
  );

  // Función para generar variaciones de color
  const generateColorVariations = (hexColor) => {
    // Convertir hex a RGB
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Generar versión oscura (20% más oscuro)
    const darker = (val) => Math.max(0, Math.floor(val * 0.8));
    const primaryDark = `#${darker(r).toString(16).padStart(2, '0')}${darker(g).toString(16).padStart(2, '0')}${darker(b).toString(16).padStart(2, '0')}`;
    
    // Generar versión clara (20% más claro)
    const lighter = (val) => Math.min(255, Math.floor(val + (255 - val) * 0.3));
    const primaryLight = `#${lighter(r).toString(16).padStart(2, '0')}${lighter(g).toString(16).padStart(2, '0')}${lighter(b).toString(16).padStart(2, '0')}`;
    
    return { primaryDark, primaryLight };
  };

  // Aplicar color personalizado al tema
  const applyCustomColor = (baseTheme, color) => {
    const { primaryDark, primaryLight } = generateColorVariations(color);
    
    return {
      ...baseTheme,
      colors: {
        ...baseTheme.colors,
        primary: color,
        primaryDark: primaryDark,
        primaryLight: primaryLight,
        sidebarActive: color,
      }
    };
  };

  // Guardar preferencia en localStorage y aplicar color personalizado
  useEffect(() => {
    localStorage.setItem('theme', themeName);
    localStorage.setItem('primaryColor', primaryColor);
    
    const baseTheme = themeName === 'Oscuro' ? darkTheme : lightTheme;
    const themedWithColor = applyCustomColor(baseTheme, primaryColor);
    setCurrentTheme(themedWithColor);
  }, [themeName, primaryColor]);

  // Escuchar cambios en la preferencia del sistema cuando está en modo Auto
  useEffect(() => {
    if (themeName === 'Auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = (e) => {
        const baseTheme = e.matches ? darkTheme : lightTheme;
        const themedWithColor = applyCustomColor(baseTheme, primaryColor);
        setCurrentTheme(themedWithColor);
      };
      
      mediaQuery.addEventListener('change', handleChange);
      
      // Aplicar tema inicial
      const baseTheme = mediaQuery.matches ? darkTheme : lightTheme;
      const themedWithColor = applyCustomColor(baseTheme, primaryColor);
      setCurrentTheme(themedWithColor);
      
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [themeName, primaryColor]);

  const toggleTheme = () => {
    setThemeName(prev => prev === 'Claro' ? 'Oscuro' : 'Claro');
  };

  const setTheme = (newTheme) => {
    // newTheme puede ser: 'Claro', 'Oscuro', 'Auto'
    setThemeName(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ 
      theme: currentTheme, 
      themeName, 
      primaryColor,
      setPrimaryColor,
      toggleTheme,
      setTheme,
      isDark: currentTheme === darkTheme
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
